// @flow
import * as React from 'react';
import classNames from 'classnames/bind';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { pathOr } from 'ramda';
import { fetchJSON } from '../../utils/http';

import Lightbox from '../../basic-components/lightbox';
import Input from '../../basic-components/input';
import Icon from '../../basic-components/icon';
import Loader from '../../basic-components/loader';
import MessageItem from './message-item';
import { User as UserQuery } from '../../shared-queries/User.graphql';
import { ConversationHistory as ConversationHistoryQuery } from './ConversationHistory.graphql';
import { ConversationChat as ConversationChatMutation } from './ConversationChat.graphql';
import { SubmitFeedback as SubmitFeedbackMutation } from './SubmitFeedback.graphql';
import DefaultAvatar from '../user/default-avatar.svg';
import t from './locale';
import cs from './styles.pcss';

const cx = classNames.bind(cs);

type Message = {
  id: string,
  type: 'user' | 'bot',
  author: string,
  text: string,
  time: string,
  state?: string,
  suggestedActions?: Array<string>,
  products?: Array<any>,
  queryParams?: ?{ [string]: string },
  publishUrl?: string,
  analysisReport?: string,
  executionInfo?: any,
  errorInfo?: any,
  intakeInfo?: any,
  feedbackMessage?: ?{
    messageId: ?string,
    isHelpful: ?boolean,
    feedbackMessage: ?string,
    feedbackTime: ?string,
  },
};

type Props = {
  opened: boolean,
  onClose: () => void,
};

export default function AiChatbox({ opened, onClose }: Props): React.Node {
  const { data } = useQuery(UserQuery);
  const accounts = pathOr([], ['viewer', 'accounts'], data);
  const username = pathOr<string, number | string, string, any>(
    'User',
    [0, 'name'],
    accounts
  );
  const viewerId = pathOr<string, string, any>(null, ['viewer', 'id'], data);

  // GraphQL hooks
  const [loadHistory] = useLazyQuery(ConversationHistoryQuery, {
    fetchPolicy: 'network-only',
  });

  const [sendChat] = useMutation(ConversationChatMutation);
  const [submitFeedbackMutation] = useMutation(SubmitFeedbackMutation);

  const [messages, setMessages] = React.useState<Array<Message>>([]);
  const [inputText, setInputText] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = React.useState(true);
  const [showScrollButton, setShowScrollButton] = React.useState(false);
  const messagesEndRef = React.useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = React.useRef<HTMLDivElement | null>(null);

  // Feedback dialog states
  const MAX_FEEDBACK_LENGTH = 2500;
  const [feedbackDialog, setFeedbackDialog] = React.useState(null);
  const [feedbackText, setFeedbackText] = React.useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = React.useState(false);
  const [submittingMessageId, setSubmittingMessageId] = React.useState(null);

  // Pagination states
  const [hasMoreHistory, setHasMoreHistory] = React.useState(true);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = React.useState(false);

  // Safety limit to prevent infinite loading
  const MAX_MESSAGES = 1000;

  // Track scroll height before loading more messages for restoration
  const scrollHeightBeforeUpdate = React.useRef<number | null>(null);

  // Refs to track latest pagination state for scroll handler
  const hasMoreHistoryRef = React.useRef(hasMoreHistory);
  const isLoadingMoreHistoryRef = React.useRef(isLoadingMoreHistory);

  // Debounce timer for scroll handler
  const scrollDebounceTimer = React.useRef<number | null>(null);

  // Flag to prevent loading immediately after scroll restoration
  const isRestoringScroll = React.useRef(false);

  // Whether the initial history load has been done (survives open/close cycles)
  const hasInitialLoadedRef = React.useRef(false);

  // Saved scroll position when modal was last closed
  const savedScrollTopRef = React.useRef<number>(0);

  // startCursor from the last pageInfo response, used as `before` for the next pagination call
  const beforeCursorRef = React.useRef<string | null>(null);


  // Keep refs in sync with state
  React.useEffect(() => {
    hasMoreHistoryRef.current = hasMoreHistory;
  }, [hasMoreHistory]);

  React.useEffect(() => {
    isLoadingMoreHistoryRef.current = isLoadingMoreHistory;
  }, [isLoadingMoreHistory]);

  const scrollToBottom = (smooth = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
      });
      setShowScrollButton(false);
    }
  };

  // Check if user is at the bottom of the messages container
  const checkIfAtBottom = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        messagesContainerRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      // Show button when user has scrolled up more than 100px from bottom
      const isScrolledUp = distanceFromBottom > 100;
      setShowScrollButton(isScrolledUp);
    }
  };

  // Load chat history on first open; preserve state on subsequent open/close cycles
  React.useEffect(() => {
    if (opened) {
      if (!hasInitialLoadedRef.current) {
        // First open: load history from server
        hasInitialLoadedRef.current = true;
        localStorage.removeItem('ai-chat-session-id');
        loadChatHistory(); // eslint-disable-line no-use-before-define
      } else {
        // Subsequent opens: restore saved scroll position, no new request.
        // Use rAF to defer until after the Lightbox finishes showing the container
        // (browsers reset scrollTop to 0 on hidden elements).
        requestAnimationFrame(() => {
          const container = messagesContainerRef.current;
          if (container) {
            container.scrollTop = savedScrollTopRef.current;
          }
        });
      }
    } else {
      // Modal closing: clear timers only.
      // savedScrollTopRef is maintained by handleMessagesScroll (refs are null here).
      // Messages and pagination state are intentionally preserved.
      if (scrollDebounceTimer.current) {
        clearTimeout(scrollDebounceTimer.current);
        scrollDebounceTimer.current = null;
      }
      isRestoringScroll.current = false;
      isLoadingMoreHistoryRef.current = false;
      setIsLoadingMoreHistory(false);
    }
  }, [opened]);

  // Restore scroll position after loading more history
  // This runs synchronously before browser paint to prevent flickering
  React.useLayoutEffect(() => {
    if (
      scrollHeightBeforeUpdate.current !== null &&
      messagesContainerRef.current
    ) {
      const container = messagesContainerRef.current;

      // Set flag to prevent loading during restoration
      isRestoringScroll.current = true;

      const previousScrollTop = container.scrollTop;
      const scrollDiff =
        container.scrollHeight - scrollHeightBeforeUpdate.current;

      // Restore to previous position
      container.scrollTop = previousScrollTop + scrollDiff;

      // Reset the ref
      scrollHeightBeforeUpdate.current = null;

      // Clear restoration flag after a short delay
      setTimeout(() => {
        isRestoringScroll.current = false;
      }, 500); // Increased from 200ms to 500ms
    }
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive (only if already near bottom or loading)
  React.useEffect(() => {
    if (isLoading || !messagesContainerRef.current) {
      return;
    }

    // Check if user is already near the bottom before auto-scrolling
    const { scrollTop, scrollHeight, clientHeight } =
      messagesContainerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isNearBottom = distanceFromBottom < 150;

    // Only auto-scroll if user is near bottom (to avoid disrupting reading old messages)
    if (isNearBottom) {
      scrollToBottom(false);
    }

    // Check scroll position after a short delay to update button visibility
    setTimeout(() => {
      checkIfAtBottom();
    }, 100);
  }, [messages]);

  // Scroll handler attached directly via onScroll prop - always uses latest closure,
  // no stale closure issues. Refs prevent concurrent loadMoreHistory calls.
  const handleMessagesScroll = () => {
    // Keep scroll position up-to-date so it can be restored on reopen
    if (messagesContainerRef.current) {
      savedScrollTopRef.current = messagesContainerRef.current.scrollTop;
    }

    checkIfAtBottom();

    if (scrollDebounceTimer.current) {
      clearTimeout(scrollDebounceTimer.current);
    }

    scrollDebounceTimer.current = setTimeout(() => {
      if (isRestoringScroll.current) {
        return;
      }

      const container = messagesContainerRef.current;
      if (!container) return;

      const isNearTop = container.scrollTop < 100;

      if (isNearTop && !isLoadingMoreHistoryRef.current && hasMoreHistoryRef.current) {
        loadMoreHistory(); // eslint-disable-line no-use-before-define
      }
    }, 150);
  };

  const getCurrentTime = () => {
    const now = new Date();
    return now.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTime = (timestamp) => {
    const messageDate = new Date(timestamp);
    const now = new Date();

    // Get time portion (HH:mm)
    const timeStr = messageDate.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    // Check if today
    const isToday =
      messageDate.getDate() === now.getDate() &&
      messageDate.getMonth() === now.getMonth() &&
      messageDate.getFullYear() === now.getFullYear();

    if (isToday) {
      // Today: show time only
      return timeStr;
    }

    // Check if yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday =
      messageDate.getDate() === yesterday.getDate() &&
      messageDate.getMonth() === yesterday.getMonth() &&
      messageDate.getFullYear() === yesterday.getFullYear();

    if (isYesterday) {
      // Yesterday: show MM-DD HH:mm (consistent with other dates this year)
      const month = String(yesterday.getMonth() + 1).padStart(2, '0');
      const day = String(yesterday.getDate()).padStart(2, '0');
      return `${month}-${day} ${timeStr}`;
    }

    // Check if this year
    const isThisYear = messageDate.getFullYear() === now.getFullYear();

    if (isThisYear) {
      // This year but not yesterday: show MM-DD HH:mm
      const month = String(messageDate.getMonth() + 1).padStart(2, '0');
      const day = String(messageDate.getDate()).padStart(2, '0');
      return `${month}-${day} ${timeStr}`;
    }

    // Last year or earlier: show YYYY-MM-DD HH:mm
    const year = messageDate.getFullYear();
    const month = String(messageDate.getMonth() + 1).padStart(2, '0');
    const day = String(messageDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day} ${timeStr}`;
  };

  const extractProducts = (response) => {
    const products = [];

    // Try to get result from direct response.result first (new format)
    let { result } = response;

    // Fallback to metadata format (old format)
    if (
      !result &&
      response.metadata?.metadataType === 'COMPLETED' &&
      response.metadata?.executionMetadata?.result
    ) {
      result = response.metadata.executionMetadata.result;
    }

    // Extract products from result if available
    if (result) {
      if (
        (result.dataType === 'product' ||
          result.dataType === 'analysis' ||
          result.dataType === 'url') &&
        result.productData?.nodes
      ) {
        products.push(...result.productData.nodes);
      }
    }

    return products;
  };

  const extractProductQueryParams = (response) => {
    // Try to get result from direct response.result first (new format)
    let { result } = response;

    // Fallback to metadata format (old format)
    if (
      !result &&
      response.metadata?.metadataType === 'COMPLETED' &&
      response.metadata?.executionMetadata?.result
    ) {
      result = response.metadata.executionMetadata.result;
    }

    if (
      result &&
      (result.dataType === 'product' ||
        result.dataType === 'analysis' ||
        result.dataType === 'url') &&
      result.productData?.queryParams
    ) {
      return result.productData.queryParams;
    }

    return null;
  };

  const extractAnalysisReport = (response) => {
    // Try to get result from direct response.result first (new format)
    let { result } = response;

    // Fallback to metadata format (old format)
    if (
      !result &&
      response.metadata?.metadataType === 'COMPLETED' &&
      response.metadata?.executionMetadata?.result
    ) {
      result = response.metadata.executionMetadata.result;
    }

    // Extract analysis report if available (regardless of dataType)
    if (result && result.analysisData?.report) {
      return result.analysisData.report;
    }

    return null;
  };

  const extractPublishUrl = (response) => {
    // Try to get result from direct response.result first (new format)
    let { result } = response;

    // Check for publish_to_global in metadata (old format)
    const hasPublishToGlobal =
      response.metadata?.executionMetadata?.toolsUsed?.includes(
        'publish_to_global'
      );

    // Fallback to metadata format (old format)
    if (
      !result &&
      hasPublishToGlobal &&
      response.metadata?.metadataType === 'COMPLETED' &&
      response.metadata?.executionMetadata?.result
    ) {
      result = response.metadata.executionMetadata.result;
    }

    // Extract URL if available (regardless of dataType)
    if (result && result.urlData?.url) {
      return result.urlData.url;
    }

    return null;
  };

  const buildAssistantMessage = (responseData, timestamp = null) => {
    // Extract data first to use in fallback message
    const products = extractProducts(responseData);
    const queryParams = extractProductQueryParams(responseData);
    const publishUrl = extractPublishUrl(responseData);
    const analysisReport = extractAnalysisReport(responseData);

    // Generate friendly fallback message based on content
    let fallbackText = t('operationCompleted');
    if (products && products.length > 0) {
      fallbackText = t('productsFound', { count: products.length });
    } else if (analysisReport) {
      fallbackText = t('analysisComplete');
    } else if (responseData.state === 'COMPLETED') {
      fallbackText = t('taskCompleted');
    } else if (responseData.state === 'ERROR') {
      fallbackText = t('taskError');
    } else if (responseData.state === 'INTAKE') {
      fallbackText = t('needMoreInfo');
    }

    const message: Message = {
      id: `bot-${Date.now()}-${Math.random()}`,
      type: 'bot',
      author: t('assistantName'),
      text: responseData.message || fallbackText,
      time: timestamp ? formatTime(timestamp) : getCurrentTime(),
      state: responseData.state,
      suggestedActions: responseData.suggestedActions || [],
      products,
      queryParams,
      publishUrl,
      analysisReport,
      executionInfo: null,
      errorInfo: null,
      intakeInfo: null,
    };

    // Add metadata based on type
    if (responseData.metadata) {
      const { metadata } = responseData;

      if (metadata.metadataType === 'COMPLETED' && metadata.executionMetadata) {
        message.executionInfo = {
          stepsExecuted: metadata.executionMetadata.stepsExecuted,
          toolsUsed: metadata.executionMetadata.toolsUsed,
          executionTimeMs: metadata.executionMetadata.executionTimeMs,
        };
      } else if (
        (metadata.metadataType === 'ERROR' ||
          metadata.metadataType === 'BLOCKED') &&
        metadata.errorMetadata
      ) {
        message.errorInfo = {
          errorCode: metadata.errorMetadata.errorCode,
          errorDetails: metadata.errorMetadata.errorDetails,
          failedStepId: metadata.errorMetadata.failedStepId,
          suggestedRecovery: metadata.errorMetadata.suggestedRecovery,
        };
      } else if (
        metadata.metadataType === 'INTAKE' &&
        metadata.intentMetadata
      ) {
        message.intakeInfo = {
          requiredInfo: metadata.intentMetadata.requiredInfo,
          suggestedQuestions: metadata.intentMetadata.suggestedQuestions,
          intentType: metadata.intentMetadata.intentType,
        };
      }
    }

    return message;
  };

  const loadChatHistory = async () => {
    // Block scroll-triggered loadMoreHistory for the entire initial load + scroll-to-bottom
    isRestoringScroll.current = true;
    setIsLoadingHistory(true);

    try {
      const { data } = await loadHistory({
        variables: { limit: 20 },
      });

      const messages = data?.conversationHistory?.messages || [];

      if (messages.length > 0) {
        const historyMessages = parseHistoryMessages(messages); // eslint-disable-line no-use-before-define

        setMessages(historyMessages);
        setHasMoreHistory(data?.conversationHistory?.pageInfo?.hasPreviousPage ?? false);
        setCurrentPage(1);

        // Store startCursor as the `before` value for the next pagination call
        beforeCursorRef.current = data?.conversationHistory?.pageInfo?.startCursor ?? null;
      } else {
        // No history, show welcome message
        setMessages([
          {
            id: 'welcome',
            type: 'bot',
            author: t('assistantName'),
            text: t('welcomeMessage', { username }),
            time: getCurrentTime(),
          },
        ]);
        setHasMoreHistory(false);
      }
    } catch (error) {
      // Show welcome message on error
      setMessages([
        {
          id: 'welcome',
          type: 'bot',
          author: t('assistantName'),
          text: t('welcomeMessage', { username }),
          time: getCurrentTime(),
        },
      ]);
      setHasMoreHistory(false);
    } finally {
      setIsLoadingHistory(false);
      // Scroll to bottom after history loads; isRestoringScroll was set at the
      // start of loadChatHistory to block scroll-triggered loadMoreHistory calls
      setTimeout(() => {
        scrollToBottom(false);
        setTimeout(() => {
          isRestoringScroll.current = false;
        }, 500);
      }, 100);
    }
  };

  const parseHistoryMessages = (historyMsgs) =>
    historyMsgs
      .map((msg, index) => {
        if (msg.role === 'user') {
          return {
            id: msg.id,
            type: 'user',
            author: t('me'),
            text: msg.content,
            time: msg.timestamp ? formatTime(msg.timestamp) : getCurrentTime(),
          };
        }
        if (msg.role === 'assistant') {
          const products = extractProducts(msg);
          const queryParams = extractProductQueryParams(msg);
          const publishUrl = extractPublishUrl(msg);
          const analysisReport = extractAnalysisReport(msg);
          return {
            id: msg.id,
            type: 'bot',
            author: t('assistantName'),
            text: msg.content,
            time: msg.timestamp ? formatTime(msg.timestamp) : getCurrentTime(),
            products,
            queryParams,
            publishUrl,
            analysisReport,
            feedbackMessage: msg.feedbackMessage || null,
          };
        }
        return null;
      })
      .filter((msg) => msg !== null);

  const loadMoreHistory = async () => {
    const container = messagesContainerRef.current;

    // Use refs only (no stale state) to prevent race conditions
    if (isLoadingMoreHistoryRef.current || !hasMoreHistoryRef.current) {
      return;
    }

    // Immediately update ref to prevent duplicate calls
    isLoadingMoreHistoryRef.current = true;
    setIsLoadingMoreHistory(true);

    // Store current scroll height before loading (reuse container variable)
    const previousScrollHeight = container?.scrollHeight || 0;

    try {
      const before = beforeCursorRef.current;

      const { data } = await loadHistory({
        variables: {
          before,
          limit: 20,
        },
      });

      const responseMessages = data?.conversationHistory?.messages || [];

      if (responseMessages.length > 0) {
        const historyMessages = parseHistoryMessages(responseMessages); // eslint-disable-line no-use-before-define

        // Update cursor with the startCursor from this response for the next pagination call
        beforeCursorRef.current = data?.conversationHistory?.pageInfo?.startCursor ?? null;

        // Store scroll height for useLayoutEffect to restore scroll position
        scrollHeightBeforeUpdate.current = previousScrollHeight;

        // Track the new total for later checks
        let newTotal = 0;
        let reachedLimit = false;

        // Update all state together - scroll restoration happens in useLayoutEffect
        setMessages((prev) => {
          newTotal = historyMessages.length + prev.length;

          // Safety check: prevent loading too many messages
          if (newTotal >= MAX_MESSAGES) {
            reachedLimit = true;
            // Keep only the most recent MAX_MESSAGES
            return [...historyMessages, ...prev].slice(0, MAX_MESSAGES);
          }

          return [...historyMessages, ...prev];
        });

        setCurrentPage((prev) => prev + 1);

        // Check if we should continue loading (use calculated newTotal, not stale messages.length)
        const hasPreviousPage = data?.conversationHistory?.pageInfo?.hasPreviousPage ?? false;
        const shouldContinue = hasPreviousPage && !reachedLimit && newTotal < MAX_MESSAGES;

        setHasMoreHistory(shouldContinue);
        hasMoreHistoryRef.current = shouldContinue;
      } else {
        setHasMoreHistory(false);
        hasMoreHistoryRef.current = false;
      }
    } catch (error) {
      // On error, stop pagination
      setHasMoreHistory(false);
      hasMoreHistoryRef.current = false;
    } finally {
      // Always reset loading state
      setIsLoadingMoreHistory(false);
      isLoadingMoreHistoryRef.current = false;
    }
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      type: 'user',
      author: t('me'),
      text: inputText.trim(),
      time: getCurrentTime(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageText = inputText.trim();
    setInputText('');
    setIsLoading(true);

    // Scroll to bottom to show typing indicator
    setTimeout(() => {
      scrollToBottom(true);
    }, 100);

    try {
      const { data } = await sendChat({
        variables: {
          message: messageText,
        },
      });

      const result = data?.conversationChat;

      if (result?.status === 'success') {
        const botMessage = buildAssistantMessage(result);
        setMessages((prev) => [...prev, botMessage]);
      } else {
        // Handle error from mutation
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          type: 'bot',
          author: t('assistantName'),
          text: t('errorMessage', {
            error: result?.errors?.[0] || t('unableToConnect'),
          }),
          time: getCurrentTime(),
          state: 'ERROR',
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch (error) {
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        type: 'bot',
        author: t('assistantName'),
        text: t('errorMessage', {
          error: error.message || t('unableToConnect'),
        }),
        time: getCurrentTime(),
        state: 'ERROR',
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const submitFeedback = async (messageId, feedbackType, feedbackTextValue) => {
    if (isSubmittingFeedback) return;
    setIsSubmittingFeedback(true);
    setSubmittingMessageId(messageId);
    try {
      const isHelpful = feedbackType === 'helpful';
      const { data } = await submitFeedbackMutation({
        variables: {
          messageId,
          isHelpful,
          feedbackMessage: feedbackTextValue || undefined,
        },
      });
      if (data?.conversationSubmitFeedback?.success) {
        handleFeedbackUpdate(messageId, isHelpful); // eslint-disable-line no-use-before-define
      } else {
        const errors = data?.conversationSubmitFeedback?.errors || [];
        // eslint-disable-next-line no-console
        console.error('Failed to submit feedback:', errors.join(', '));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to send feedback:', error);
    } finally {
      setIsSubmittingFeedback(false);
      setSubmittingMessageId(null);
    }
  };

  const handleHelpfulClick = (messageId) => {
    submitFeedback(messageId, 'helpful');
  };

  const handleNotHelpfulClick = (messageId) => {
    setFeedbackDialog({ messageId });
  };

  const handleFeedbackDialogClose = () => {
    setFeedbackDialog(null);
    setFeedbackText('');
  };

  const handleFeedbackUpdate = (messageId: string, isHelpful: boolean) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              feedbackMessage: {
                ...msg.feedbackMessage,
                messageId,
                isHelpful,
                feedbackTime: new Date().toISOString(),
              },
            }
          : msg
      )
    );
  };

  const handleSuggestedAction = (action: string) => {
    setInputText(action);
    // Auto-send after a short delay to show the input
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };

  const handlePublishClick = React.useCallback((queryParams) => {
    // Fire-and-forget stats ping; do not block navigation
    fetchJSON('/conversation/publish_click', {
      method: 'POST',
      body: {
        query_params: queryParams || {},
      },
    }).catch(() => {
      // Silently ignore — stats ping is non-critical
    });
  }, []);

  const handleInputChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
  };

  const renderInputArea = () => (
    <div className={cx('input-container')}>
      <Input
        value={inputText}
        onChange={handleInputChange}
        placeholder={t('inputPlaceholder')}
        disabled={isLoading}
        size="medium"
      />
      <button
        type="button"
        onClick={handleSendMessage}
        disabled={!inputText.trim() || isLoading}
        className={cx('send-button', {
          active: inputText.trim() && !isLoading,
        })}
        aria-label={t('sendButton')}
      >
        <Icon
          name="ebayAi"
          className={cx('send-icon')}
        />
        <span className={cx('send-text')}>{t('sendButton')}</span>
      </button>
    </div>
  );

  return (
    <>
    <Lightbox
      opened={feedbackDialog !== null}
      header={t('feedbackDialogHeader')}
      lightboxName="ai-feedback-dialog"
      onClose={handleFeedbackDialogClose}
      small
      footer={(closeOverlay) => (
        <div className="ai-chat-feedback-dialog-footer">
          <button
            type="button"
            className="ai-chat-feedback-dialog-cancel"
            onClick={() => { handleFeedbackDialogClose(); closeOverlay(); }}
          >
            {t('feedbackDialogCancel')}
          </button>
          <button
            type="button"
            className="ai-chat-feedback-dialog-submit"
            onClick={async () => {
              await submitFeedback(feedbackDialog?.messageId, 'not-helpful', feedbackText);
              closeOverlay();
            }}
            disabled={isSubmittingFeedback}
          >
            {t('feedbackDialogSubmit')}
          </button>
        </div>
      )}
    >
      <div className="ai-chat-feedback-dialog-content">
        <p className="ai-chat-feedback-dialog-description">
          {t('feedbackDialogDescription')}
        </p>
        <div className="ai-chat-feedback-dialog-field">
          <label className="ai-chat-feedback-dialog-label" htmlFor="ai-feedback-text">
            {t('feedbackDialogFeedback')}
          </label>
          <div className="ai-chat-feedback-dialog-textarea-wrapper">
            <textarea
              id="ai-feedback-text"
              className="ai-chat-feedback-dialog-textarea"
              placeholder={t('feedbackDialogFeedbackPlaceholder')}
              value={feedbackText}
              maxLength={MAX_FEEDBACK_LENGTH}
              onChange={(e) => setFeedbackText(e.target.value)}
            />
            <span className="ai-chat-feedback-dialog-counter">
              {feedbackText.length}/{MAX_FEEDBACK_LENGTH}
            </span>
          </div>
        </div>
      </div>
    </Lightbox>
    <Lightbox
      opened={opened}
      onClose={onClose}
      header={t('header')}
      lightboxName="ai-chatbox"
      className={cx('chatbox-lightbox')}
      footer={renderInputArea()}
      contentIsOutOfFlow={true}
    >
      <div className={cx('chatbox-container')}>
        {/* Header with subtitle and user info */}
        <div className={cx('chat-header')}>
          <div className={cx('header-content')}>
            <div className={cx('subtitle-wrapper')}>
              <p className={cx('subtitle-text')}>{t('subtitle')}</p>
              <p className={cx('disclaimer')}>{t('disclaimer')}</p>
            </div>
            <div className={cx('user-info')}>
              <span className={cx('username')}>{username}</span>
              <DefaultAvatar className={cx('user-icon')} />
            </div>
          </div>
        </div>

        {/* Messages container */}
        <div className={cx('messages-container')} ref={messagesContainerRef} onScroll={handleMessagesScroll}>
          {/* Loading history indicator */}
          {isLoadingHistory && (
            <div className={cx('loading-history')}>
              <Loader size={42} />
              <span>{t('loadingHistory')}</span>
            </div>
          )}

          {/* Loading more history indicator at top */}
          {!isLoadingHistory && isLoadingMoreHistory && (
            <div className={cx('loading-more-history')}>
              <Loader size={24} />
              <span>{t('loadingMoreHistory')}</span>
            </div>
          )}

          {/* Show "No more messages" if at the end */}
          {!isLoadingHistory &&
            !isLoadingMoreHistory &&
            !hasMoreHistory &&
            messages.length > 0 && (
              <div className={cx('no-more-messages')}>
                {t('noMoreMessages')}
              </div>
            )}

          {/* Messages list */}
          {!isLoadingHistory &&
            messages.map((message) => (
              <MessageItem
                key={message.id}
                message={message}
                queryParams={message.queryParams}
                onSuggestedAction={handleSuggestedAction}
                onHelpfulClick={handleHelpfulClick}
                onNotHelpfulClick={handleNotHelpfulClick}
                onPublishClick={handlePublishClick}
                isSubmitting={submittingMessageId === message.id && isSubmittingFeedback}
              />
            ))}

          {/* Typing indicator */}
          {isLoading && (
            <div className={cx('message', 'bot')}>
              <div className={cx('message-content')}>
                <div className={cx('typing-indicator')}>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Scroll to bottom button - positioned outside messages-container */}
        {showScrollButton && (
          <button
            type="button"
            className={cx('scroll-to-bottom-btn')}
            onClick={() => {
              scrollToBottom(true);
            }}
            aria-label={t('scrollToBottom')}
          >
            <Icon name="arrowDownScroll" className={cx('scroll-icon')} />
          </button>
        )}
      </div>
    </Lightbox>
    </>
  );
}
